import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '@savgroup-front-common/core/formatters';
import { Spinner } from '@savgroup-front-common/primitive/ui/spinner';
import {
  APP_COMPONENT_STATUSES,
  AppComponent,
  AppScheduledMaintenance,
} from '@savgroup-front-common/types';

import getNextScheduledMaintenance from '@/view/molecules/AppStatuses/helpers/getNextScheduledMaintenance';

import messages from './messages';

const STATUS_DOT_CLASSNAME_BY_STATUS: Record<APP_COMPONENT_STATUSES, string> = {
  [APP_COMPONENT_STATUSES.OPERATIONAL]: 'bg-icon-success-default',
  [APP_COMPONENT_STATUSES.DEGRADED_PERFORMANCE]: 'bg-icon-warning-default',
  [APP_COMPONENT_STATUSES.PARTIAL_OUTAGE]: 'bg-icon-warning-default',
  [APP_COMPONENT_STATUSES.MAJOR_OUTAGE]: 'bg-icon-danger-default',
  [APP_COMPONENT_STATUSES.UNDER_MAINTENANCE]: 'bg-icon-primary-default',
};

interface HeaderProfileAppStatusProps {
  appScheduledMaintences: AppScheduledMaintenance[];
  appComponents: AppComponent[];
}

const HeaderProfileAppStatus: FC<HeaderProfileAppStatusProps> = ({
  appScheduledMaintences,
  appComponents,
}) => {
  const { formatDate } = useIntl();

  const getComponentStatusLabel = useCallback(
    (appComponent: AppComponent) => {
      switch (appComponent.status) {
        case APP_COMPONENT_STATUSES.DEGRADED_PERFORMANCE: {
          return {
            statusLabel: safeFormattedIntlString(messages.degradedPerformance),
            hasScheduledMaintenance: false,
          };
        }
        case APP_COMPONENT_STATUSES.MAJOR_OUTAGE: {
          return {
            statusLabel: safeFormattedIntlString(messages.majorOutage),
            hasScheduledMaintenance: false,
          };
        }
        case APP_COMPONENT_STATUSES.OPERATIONAL: {
          const nextScheduledMaintenance = getNextScheduledMaintenance(
            appScheduledMaintences,
            appComponent.name,
          );

          if (nextScheduledMaintenance) {
            return {
              statusLabel: safeFormattedIntlString(
                messages.nextScheduledMaintenance,
                {
                  time: formatDate(nextScheduledMaintenance.scheduledFor, {
                    day: 'numeric',
                    month: 'long',
                    hour: 'numeric',
                    minute: 'numeric',
                  }),
                },
              ),
              hasScheduledMaintenance: true,
            };
          }

          return {
            statusLabel: safeFormattedIntlString(messages.operational),
            hasScheduledMaintenance: false,
          };
        }
        case APP_COMPONENT_STATUSES.PARTIAL_OUTAGE: {
          return {
            statusLabel: safeFormattedIntlString(messages.partialOutage),
            hasScheduledMaintenance: false,
          };
        }
        case APP_COMPONENT_STATUSES.UNDER_MAINTENANCE: {
          return {
            statusLabel: safeFormattedIntlString(messages.underMaintenance),
            hasScheduledMaintenance: false,
          };
        }

        default:
          return {
            statusLabel: safeFormattedIntlString(messages.loading),
            hasScheduledMaintenance: false,
          };
      }
    },
    [appScheduledMaintences, formatDate],
  );

  const hasComponents = appComponents.length > 0;

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-semibold stext-base-default">
        <SafeFormattedMessageWithoutSpread message={messages.appStatus} />
      </p>

      {!hasComponents && (
        <div className="flex items-center gap-2 text-xs stext-muted-default">
          <Spinner className="size-3" />
          {safeFormattedIntlString(messages.loading)}
        </div>
      )}

      {hasComponents && (
        <div className="flex flex-col gap-1">
          {appComponents.map((appComponent) => {
            const { hasScheduledMaintenance, statusLabel } =
              getComponentStatusLabel(appComponent);

            const dotClassName = hasScheduledMaintenance
              ? 'bg-icon-primary-default'
              : STATUS_DOT_CLASSNAME_BY_STATUS[appComponent.status];

            return (
              <div
                key={appComponent.id}
                className="flex items-center gap-2 text-xs stext-muted-default"
              >
                <span
                  className={`size-2 shrink-0 rounded-full ${dotClassName}`}
                />
                <span className="truncate">
                  {appComponent.name} -{' '}
                  <strong className="font-semibold stext-base-default">
                    {statusLabel}
                  </strong>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HeaderProfileAppStatus;
